import React, { ReactElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Node } from '../Node';
import { useConnection } from '../Connections/hooks/use-connection';
import { useDrag } from '../Node/hooks/use-drag';
import { usePosition } from '../Node/hooks/use-position';
import { CommonIconEditModalContainer } from '../CommonIconEditModal/container';
import { COMMON_DIAGRAM_ICONS } from '../../constants';
import {
  ANYCONNECT_ICON_PICKER_ICONS,
  ANYCONNECT_TAB_ORDER,
} from '../../../../constants/iconPicker';
import { CommonIconEditButtonContainer } from '../CommonIconEditButton/container';
import styles from './AnyconnectNode.module.css';
import { ReactComponent as CiscoSecureClientIcon } from './images/CiscoSecureClient.svg';
import { DefaultAnyconnectNodeDisplay as defaultPosition } from './constants';
import { determineIconToRender } from './utils/utils';

export function AnyconnectNode({
  uid,
  display,
  isReadOnly,
  isSnapToGridEnabled,
  isAddingLogicalConnection,
}: BaseNodeProps): ReactElement {
  const nodeRef = useRef<HTMLDivElement>(null);
  const { redrawNodeConnections } = useConnection();
  const { t } = useTranslation();

  const hookProps = {
    defaultPosition,
    display,
    nodeRef,
    redrawNodeConnections,
    uid,
  };

  usePosition({ ...hookProps });
  useDrag({ ...hookProps, isReadOnly, isSnapToGridEnabled });

  const DEFAULT_EXISTING_ICON = 'Laptop';

  const TITLE_CLASSES = classNames(styles.title, {
    [styles.hiddenOpacity]: display?.hidden,
  });

  const SVG_CLASSES = classNames({
    [styles.svgOpacity]: display?.hidden,
  });

  const ACTIONS_BUTTON_WRAPPER_CLASSES = classNames(
    styles.actionButtonsWrapper,
    {
      [styles.hiddenOpacity]: display?.hidden,
    },
  );

  const ANY_CONNECT_ICON_CLASSES = classNames(styles.anyConnectSvgCorrection, {
    [styles.anyconnectIconOpacityApplied]: display?.hidden,
  });

  return (
    <>
      <Node
        isCommonNode={true}
        isHidden={display?.hidden}
        className={styles.root}
        ref={nodeRef}
        isReadOnly={isReadOnly}
        uid={uid}
        isAddingLogicalConnection={isAddingLogicalConnection}
        isConnectionAllowed={false}
        iconName={display?.icon}
      >
        <p className={TITLE_CLASSES}>{t('diagram.anyconnect')}</p>
        <div className={ACTIONS_BUTTON_WRAPPER_CLASSES}>
          <CommonIconEditButtonContainer
            isAddingLogicalConnection={isAddingLogicalConnection || false}
            isReadOnly={isReadOnly}
            name={COMMON_DIAGRAM_ICONS.ANYCONNECT}
          />
        </div>
        <span className={SVG_CLASSES}>
          {determineIconToRender({ display, uid })}
        </span>
        <div className={ANY_CONNECT_ICON_CLASSES}>
          <CiscoSecureClientIcon className={styles.anyconnectIcon} />
        </div>
      </Node>
      <CommonIconEditModalContainer
        nodeUid={uid}
        display={display}
        availableIcons={ANYCONNECT_ICON_PICKER_ICONS}
        tabOrder={ANYCONNECT_TAB_ORDER}
        existingIcon={display?.icon || DEFAULT_EXISTING_ICON}
        nodeType={COMMON_DIAGRAM_ICONS.ANYCONNECT}
      />
    </>
  );
}
